import { getJWTToken } from './auth';
import { getJournalApiBase as getApiBase, buildAuthHeaders } from './journalService';

// ==================== Types ====================

export type StreakData = {
  current_streak: number;
  longest_streak: number;
  total_checkin_days: number;
  today_checked_in: boolean;
};

export type CalendarDay = {
  date: string;        // "2026-04-01"
  emoji: string | null;
  color: string | null;
  emotion_label: string | null;
  source: string | null; // "conversation" | "manual" | null
};

export type CheckInListItem = {
  id: string;
  type: 'conversation_auto' | 'manual_entry';
  emotion_tag: string;
  emotion_category: string | null;
  emoji: string;
  color: string;                     // hex
  activity_tag: string | null;
  activity_emoji: string | null;
  journal_title: string | null;
  journal_text: string | null;
  detected_emotions: { emotion: string; percentage: number; color?: string }[] | null;
  persona_id: string | null;
  local_date: string;                // "2026-04-07"
  created_at: number;                // ms timestamp
  source_conversation_id: string | null;
};

export type TranscriptTurn = {
  turn_index: number;
  speaker: string;     // "user" | "lumi"
  text: string;
  timestamp: string;   // ISO UTC string
};

export type TranscriptData = {
  conversation_id: string;
  title: string | null;
  turns: TranscriptTurn[];
};

export type ExploreInsightStatus = 'ready' | 'not_ready';

export type ExploreInsightItem = {
  insight_type: string;
  display_title: string;
  emoji: string;
  status: ExploreInsightStatus;
  pattern: string | null;
  signals: string[] | null;
  checkin_count: number | null;
  conversation_count: number | null;
};

export type ExploreInsightsResponse = {
  items: ExploreInsightItem[];
  total_checkin_days: number;
};

/** Daily mood point for the library weekly line chart (GET /api/explore/weekly-trend). */
export type WeeklyTrendDay = {
  date: string;
  day_label: string;
  score: number | null;
  has_data: boolean;
};

export type WeeklyTrendResponse = {
  week_start: string;
  week_end: string;
  days: WeeklyTrendDay[];
  is_locked: boolean;
  days_until_unlock: number;
};

/** Pie chart row from GET /api/explore/emotion-detection (latest analyzed conversation). */
export type EmotionDetectionItem = {
  emotion: string;
  emoji: string | null;
  color: string | null;
  count: number;
  percentage: number;
};

export type EmotionDetectionResponse = {
  /** Reference date for the analysis (not necessarily a week Monday). */
  week_start: string;
  emotions: EmotionDetectionItem[];
  total_records: number;
};

// ==================== API Calls ====================

export async function fetchStreak(timezone: string): Promise<StreakData | null> {
  const token = getJWTToken();
  if (!token) return null;

  try {
    const res = await fetch(
      `${getApiBase()}/api/check-in/streak?timezone=${encodeURIComponent(timezone)}`,
      { headers: buildAuthHeaders() }
    );
    if (!res.ok) return null;
    return await res.json();
  } catch (e) {
    console.error('[exploreService] fetchStreak failed:', e);
    return null;
  }
}

export async function fetchCalendar(
  params: { year: number; month: number } | { week_start: string },
  timezone: string
): Promise<CalendarDay[]> {
  const token = getJWTToken();
  if (!token) return [];

  let url = `${getApiBase()}/api/journal/calendar?timezone=${encodeURIComponent(timezone)}`;
  if ('year' in params) {
    url += `&year=${params.year}&month=${params.month}`;
  } else {
    url += `&week_start=${encodeURIComponent(params.week_start)}`;
  }

  try {
    const res = await fetch(url, { headers: buildAuthHeaders() });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data?.days) ? data.days : [];
  } catch (e) {
    console.error('[exploreService] fetchCalendar failed:', e);
    return [];
  }
}

export async function fetchTranscript(conversationId: string): Promise<TranscriptData | null> {
  const token = getJWTToken();
  if (!token) return null;

  try {
    const res = await fetch(
      `${getApiBase()}/api/journal/transcript/${encodeURIComponent(conversationId)}`,
      { headers: buildAuthHeaders() }
    );
    if (!res.ok) return null;
    return await res.json();
  } catch (e) {
    console.error('[exploreService] fetchTranscript failed:', e);
    return null;
  }
}

export async function fetchCheckInList(
  year: number,
  month: number,
  timezone: string,
): Promise<CheckInListItem[]> {
  const token = getJWTToken();
  if (!token) return [];

  try {
    const res = await fetch(
      `${getApiBase()}/api/check-in/list?year=${year}&month=${month}&timezone=${encodeURIComponent(timezone)}`,
      { headers: buildAuthHeaders() },
    );
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data?.items) ? data.items : [];
  } catch (e) {
    console.error('[exploreService] fetchCheckInList failed:', e);
    return [];
  }
}

export type ManualCheckInPayload = {
  emotion_tag: string;
  emotion_category?: string;
  /** 用户选择的场景/事件，如 Work、Family */
  activity_tag?: string;
  /** 自定义事件等场景下的展示用 emoji */
  activity_emoji?: string;
  journal_title?: string;
  journal_text?: string;
  timezone: string;
};

export type ManualCheckInResult = {
  id: string;
  local_date: string;
  type: string;
  valence_score?: number | null;
  emoji?: string | null;
  color?: string | null;
  emotion_category?: string | null;
  streak?: {
    current_streak: number;
    longest_streak: number;
    total_checkin_days: number;
  };
};

export async function submitManualCheckIn(
  payload: ManualCheckInPayload,
): Promise<ManualCheckInResult | null> {
  const token = getJWTToken();
  if (!token) return null;

  try {
    const res = await fetch(`${getApiBase()}/api/check-in`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      console.error('[exploreService] submitManualCheckIn failed:', res.status, text);
      return null;
    }
    return await res.json();
  } catch (e) {
    console.error('[exploreService] submitManualCheckIn failed:', e);
    return null;
  }
}

export async function fetchCheckInByConversation(
  conversationId: string,
): Promise<CheckInListItem | null> {
  const token = getJWTToken();
  if (!token) return null;

  try {
    const res = await fetch(
      `${getApiBase()}/api/check-in/by-conversation/${encodeURIComponent(conversationId)}`,
      { headers: buildAuthHeaders() },
    );
    if (!res.ok) return null;
    return await res.json();
  } catch (e) {
    console.error('[exploreService] fetchCheckInByConversation failed:', e);
    return null;
  }
}

/** Valence scores from the API are in [-1, 1]; chart expects [0, 1] with top = positive. */
export function valenceToMoodChart01(score: number): number {
  return Math.min(1, Math.max(0, (score + 1) / 2));
}

export async function fetchWeeklyMoodTrend(
  timezone: string,
  weekStart?: string,
): Promise<WeeklyTrendResponse | null> {
  const token = getJWTToken();
  if (!token) return null;

  let url = `${getApiBase()}/api/explore/weekly-trend?timezone=${encodeURIComponent(timezone)}`;
  if (weekStart) {
    url += `&week_start=${encodeURIComponent(weekStart)}`;
  }

  try {
    const res = await fetch(url, { headers: buildAuthHeaders() });
    if (!res.ok) return null;
    return await res.json();
  } catch (e) {
    console.error('[exploreService] fetchWeeklyMoodTrend failed:', e);
    return null;
  }
}

export async function fetchEmotionDetection(): Promise<EmotionDetectionResponse | null> {
  const token = getJWTToken();
  if (!token) return null;

  const url = `${getApiBase()}/api/explore/emotion-detection`;

  try {
    const res = await fetch(url, { headers: buildAuthHeaders() });
    if (!res.ok) return null;
    return await res.json();
  } catch (e) {
    console.error('[exploreService] fetchEmotionDetection failed:', e);
    return null;
  }
}

export async function fetchExploreInsights(): Promise<ExploreInsightsResponse | null> {
  const token = getJWTToken();
  if (!token) return null;

  try {
    const res = await fetch(`${getApiBase()}/api/explore/insights`, {
      headers: buildAuthHeaders(),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data || !Array.isArray(data.items)) return null;
    return {
      items: data.items as ExploreInsightItem[],
      total_checkin_days:
        typeof data.total_checkin_days === 'number' ? data.total_checkin_days : 0,
    };
  } catch (e) {
    console.error('[exploreService] fetchExploreInsights failed:', e);
    return null;
  }
}

/** 获取用户 IANA 时区字符串 */
export function getUserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}
